import React from "react";
import { AlertCircle } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useUnsavedChangesStore } from "@/stores/unsavedChangesStore";

/**
 * Global unsaved-changes confirmation dialog. Mount once near the app shell.
 * Replaces the previous `window.confirm` system prompt with an in-app dialog
 * that lists every section currently holding unsaved edits.
 */
export const UnsavedChangesDialog: React.FC = () => {
  const open = useUnsavedChangesStore((s) => s.confirmOpen);
  const dirtyKeys = useUnsavedChangesStore((s) => s.dirtyKeys);
  const resolve = useUnsavedChangesStore((s) => s.resolveConfirm);

  const labels = React.useMemo(() => {
    // Stable, alphabetised list so the dialog rendering doesn't jitter.
    return Object.values(dirtyKeys)
      .map((k) => k.label)
      .sort((a, b) => a.localeCompare(b));
  }, [dirtyKeys]);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // Closing via Escape, outside-click, or X resolves as "Keep editing".
        if (!next) resolve(false);
      }}
    >
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 z-h2 text-foreground">
            <AlertCircle className="size-5 text-warning" />
            Leave without saving?
          </DialogTitle>
          <DialogDescription>
            You have unsaved changes in the sections below. If you leave now,
            those changes will be lost.
          </DialogDescription>
        </DialogHeader>

        {labels.length > 0 && (
          <ul className="space-y-2 py-1">
            {labels.map((label) => (
              <li
                key={label}
                className="flex items-center gap-3 rounded-lg border border-border bg-muted/30 p-3"
              >
                <span
                  className="size-1.5 shrink-0 rounded-full bg-warning"
                  aria-hidden
                />
                <span className="z-body text-foreground">{label}</span>
              </li>
            ))}
          </ul>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => resolve(false)}>
            Stay on this page
          </Button>
          <Button variant="destructive" onClick={() => resolve(true)}>
            Leave without saving
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
